import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Check, Minus, Plus, ArrowRight, ArrowLeft, MessageCircle, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import Layout from '../layout/Layout';
import roseCatalog from '../../data/roseCatalog';
import { pricingConfig, calculatePrice } from '../../config/pricingConfig';
import { generateOrderMessage, generateWhatsAppURL } from '../../utils/whatsappGenerator';
import { getDeliveryQuote, createOrder, getPaymentStatus } from '../../services/api';

const BuildPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [stemLength, setStemLength] = useState(60);
  const [quantity, setQuantity] = useState(25);
  const [premiumWrap, setPremiumWrap] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [address, setAddress] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [preferredDateTime, setPreferredDateTime] = useState('');
  const [notes, setNotes] = useState('');
  
  // Delivery quote state
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [fetchingQuote, setFetchingQuote] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState(null);
  
  // Payment status
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  
  const builderTopRef = useRef(null);
  const addressDebounceRef = useRef(null);

  // Check if payments are enabled on mount
  useEffect(() => {
    getPaymentStatus().then(data => {
      setPaymentsEnabled(data.paymentsEnabled);
    });
  }, []);

  // Fetch delivery quote when address changes
  useEffect(() => {
    if (deliveryMethod !== 'delivery' || !address.trim()) {
      setDeliveryQuote(null);
      setDeliveryNote(null);
      return;
    }

    // Debounce the API call
    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }

    addressDebounceRef.current = setTimeout(async () => {
      setFetchingQuote(true);
      try {
        const quote = await getDeliveryQuote(address);
        setDeliveryQuote(quote);
        setDeliveryNote(quote.note || null);
      } catch (error) {
        console.error('Delivery quote error:', error);
        setDeliveryQuote({ deliveryPrice: 10 });
        setDeliveryNote('Final delivery fee confirmed via WhatsApp');
      } finally {
        setFetchingQuote(false);
      }
    }, 800);

    return () => {
      if (addressDebounceRef.current) {
        clearTimeout(addressDebounceRef.current);
      }
    };
  }, [address, deliveryMethod]);

  // Scroll to top of builder section
  const scrollToTop = useCallback(() => {
    if (builderTopRef.current) {
      builderTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // Handle step change with scroll
  const handleStepChange = useCallback((newStep) => {
    setCurrentStep(newStep);
    setTimeout(scrollToTop, 50);
  }, [scrollToTop]);

  // Calculate pricing with delivery quote
  const pricing = useMemo(() => {
    const isDelivery = deliveryMethod === 'delivery';
    const basePrice = pricingConfig.sizePrices[stemLength] * (quantity / 25);
    const wrapPrice = premiumWrap ? pricingConfig.premiumWrap.price : 0;
    
    let deliveryPrice = 0;
    if (isDelivery) {
      // Use quote if available, otherwise use minimum
      deliveryPrice = deliveryQuote?.deliveryPrice ?? pricingConfig.delivery.minimum;
    }
    
    return {
      basePrice,
      wrapPrice,
      deliveryFee: deliveryPrice,
      total: basePrice + wrapPrice + deliveryPrice,
    };
  }, [stemLength, quantity, premiumWrap, deliveryMethod, deliveryQuote]);

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedVariety !== null;
      case 2: return stemLength !== null;
      case 3: return quantity >= 25;
      case 4: return true;
      case 5: return deliveryMethod !== null;
      default: return true;
    }
  };

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    if (newQty >= pricingConfig.quantity.min && newQty <= pricingConfig.quantity.max) {
      setQuantity(newQty);
    }
  };

  const handleSendOrder = async () => {
    const selectedRose = roseCatalog.find(r => r.id === selectedVariety);
    
    // Build order data for API
    const orderData = {
      roseVariety: selectedRose?.name || 'Not selected',
      stemLength,
      quantity,
      wrapOption: premiumWrap ? 'PREMIUM_WRAP' : 'NO_WRAP',
      fulfillmentMethod: deliveryMethod === 'delivery' ? 'DELIVERY' : 'PICKUP',
      deliveryAddress: deliveryMethod === 'delivery' ? address : null,
      customerName: customerName || null,
      customerPhone: customerPhone || null,
      preferredDateTime: preferredDateTime || null,
      notes: notes || null,
    };

    // WhatsApp message details
    const messageDetails = {
      variety: selectedRose?.name || 'Not selected',
      stemLength,
      quantity,
      premiumWrap,
      deliveryMethod,
      address: deliveryMethod === 'delivery' ? address : '',
      customerName,
      customerPhone,
      preferredDateTime,
      notes,
      estimatedTotal: pricing.total,
    };

    // Try to save order to backend first (non-blocking)
    setSavingOrder(true);
    try {
      await createOrder(orderData);
      console.log('Order saved successfully');
    } catch (error) {
      // Log error but don't block WhatsApp flow
      console.error('Failed to save order (continuing to WhatsApp):', error);
    } finally {
      setSavingOrder(false);
    }

    // Always open WhatsApp (even if order save failed)
    const message = generateOrderMessage(messageDetails);
    const url = generateWhatsAppURL(message);
    window.open(url, '_blank');
  };

  const steps = [
    { num: 1, title: 'Rose Variety' },
    { num: 2, title: 'Stem Length' },
    { num: 3, title: 'Quantity' },
    { num: 4, title: 'Wrapping' },
    { num: 5, title: 'Delivery' },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F7F2EE] via-[#FFF8F5] to-[#F7F2EE]">
        <div ref={builderTopRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-[#FB7185] mb-2">
              Build Your Bouquet
            </h1>
            <p className="text-stone-600">Create your perfect rose arrangement in 5 easy steps</p>
          </div>

          {/* Progress Steps */}
          <div className="hidden md:flex items-center justify-center gap-2 mb-10">
            {steps.map((step, index) => (
              <React.Fragment key={step.num}>
                <button
                  onClick={() => step.num <= currentStep && handleStepChange(step.num)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    currentStep === step.num
                      ? 'bg-[#F5BCB1] text-stone-900 shadow-lg shadow-[#F5BCB1]/25'
                      : step.num < currentStep
                      ? 'bg-white/70 text-[#FB7185] border border-rose-200/70 shadow-[0_10px_28px_rgba(120,72,54,0.08)] hover:bg-white/90'
                      : 'bg-white/52 text-stone-500 border border-rose-200/50'
                  }`}
                  disabled={step.num > currentStep}
                >
                  <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">
                    {step.num < currentStep ? <Check className="w-3 h-3" /> : step.num}
                  </span>
                  {step.title}
                </button>
                {index < steps.length - 1 && (
                  <div className={`w-8 h-0.5 ${step.num < currentStep ? 'bg-[#FB7185]/50' : 'bg-rose-200/70'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Mobile Progress */}
          <div className="md:hidden mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-stone-600">Step {currentStep} of 5</span>
              <span className="text-sm text-[#FB7185] font-medium">{steps[currentStep - 1].title}</span>
            </div>
            <div className="h-2 bg-white/70 rounded-full overflow-hidden border border-rose-200/60">
              <div 
                className="h-full bg-[#FB7185] rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 5) * 100}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Step 1: Rose Variety */}
              {currentStep === 1 && (
                <div>
                  <h2 className="font-serif text-xl md:text-2xl font-semibold text-[#3A2A2A] mb-6">
                    Choose Your Rose Variety
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {roseCatalog.map((rose) => (
                      <button
                        key={rose.id}
                        onClick={() => setSelectedVariety(rose.id)}
                        className={`relative rounded-2xl overflow-hidden border-2 transition-all duration-200 ${
                          selectedVariety === rose.id
                            ? 'border-[#FB7185] ring-2 ring-[#FB7185]/25 shadow-[0_18px_45px_rgba(251,113,133,0.18)]'
                            : 'border-rose-200/70 hover:border-[#FB7185]/50 shadow-[0_12px_34px_rgba(120,72,54,0.08)]'
                        }`}
                      >
                        <div className="aspect-square bg-white/70 relative">
                          <img
                            src={rose.photoPath}
                            alt={rose.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.opacity = '0';
                            }}
                          />
                        </div>
                        <div className="p-3 bg-white/78 backdrop-blur-xl">
                          <p className="font-medium text-[#3A2A2A] text-sm">{rose.name}</p>
                        </div>
                        {selectedVariety === rose.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-[#F5BCB1] rounded-full flex items-center justify-center shadow-lg">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Stem Length */}
              {currentStep === 2 && (
                <div>
                  <h2 className="font-serif text-xl md:text-2xl font-semibold text-[#3A2A2A] mb-6">
                    Choose Stem Length
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {pricingConfig.stemLengths.map((length) => (
                      <button
                        key={length}
                        onClick={() => setStemLength(length)}
                        className={`p-6 rounded-2xl border-2 transition-all duration-200 text-center ${
                          stemLength === length
                            ? 'border-[#FB7185] bg-white/82 shadow-[0_18px_45px_rgba(251,113,133,0.16)]'
                            : 'border-rose-200/70 hover:border-[#FB7185]/50 bg-white/62 shadow-[0_12px_34px_rgba(120,72,54,0.08)]'
                        }`}
                      >
                        <p className="font-serif text-3xl font-semibold text-[#3A2A2A] mb-1">{length}</p>
                        <p className="text-sm text-stone-600 mb-2">centimeters</p>
                        <p className="text-lg font-semibold text-[#FB7185]">
                          ${pricingConfig.sizePrices[length]}
                        </p>
                        <p className="text-xs text-stone-500">for 25 stems</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Quantity */}
              {currentStep === 3 && (
                <div>
                  <h2 className="font-serif text-xl md:text-2xl font-semibold text-[#3A2A2A] mb-6">
                    Choose Quantity
                  </h2>
                  <Card className="border-rose-200/70 bg-white/68 shadow-[0_14px_40px_rgba(120,72,54,0.10)] backdrop-blur-xl">
                    <CardContent className="p-8">
                      <div className="flex items-center justify-center gap-6">
                        <button
                          onClick={() => handleQuantityChange(-25)}
                          disabled={quantity <= pricingConfig.quantity.min}
                          className="w-14 h-14 rounded-full border-2 border-rose-200/80 bg-white/70 flex items-center justify-center text-stone-600 hover:border-[#FB7185] hover:text-[#FB7185] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <Minus className="w-6 h-6" />
                        </button>
                        <div className="text-center min-w-[120px]">
                          <p className="font-serif text-5xl font-semibold text-[#3A2A2A]">{quantity}</p>
                          <p className="text-stone-600 mt-1">stems</p>
                        </div>
                        <button
                          onClick={() => handleQuantityChange(25)}
                          disabled={quantity >= pricingConfig.quantity.max}
                          className="w-14 h-14 rounded-full border-2 border-rose-200/80 bg-white/70 flex items-center justify-center text-stone-600 hover:border-[#FB7185] hover:text-[#FB7185] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <Plus className="w-6 h-6" />
                        </button>
                      </div>
                      <p className="text-center text-sm text-stone-500 mt-6">
                        Bouquets are available in increments of 25 stems
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Step 4: Wrapping */}
              {currentStep === 4 && (
                <div>
                  <h2 className="font-serif text-xl md:text-2xl font-semibold text-[#3A2A2A] mb-6">
                    Choose Wrapping
                  </h2>
                  <div className="space-y-4">
                    <button
                      onClick={() => setPremiumWrap(false)}
                      className={`w-full p-6 rounded-2xl border-2 transition-all duration-200 text-left flex items-start gap-4 ${
                        !premiumWrap
                          ? 'border-[#FB7185] bg-white/82 shadow-[0_18px_45px_rgba(251,113,133,0.16)]'
                          : 'border-rose-200/70 hover:border-[#FB7185]/50 bg-white/62 shadow-[0_12px_34px_rgba(120,72,54,0.08)]'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        !premiumWrap ? 'border-[#FB7185] bg-[#FB7185]' : 'border-rose-200/90 bg-white/70'
                      }`}>
                        {!premiumWrap && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div>
                        <p className="font-semibold text-[#3A2A2A]">No Wrap</p>
                        <p className="text-sm text-stone-600 mt-1">Ribbon bow included — simple elegance</p>
                        <p className="text-[#FB7185] font-semibold mt-2">Free</p>
                      </div>
                    </button>

                    <button
                      onClick={() => setPremiumWrap(true)}
                      className={`w-full p-6 rounded-2xl border-2 transition-all duration-200 text-left flex items-start gap-4 ${
                        premiumWrap
                          ? 'border-[#FB7185] bg-white/82 shadow-[0_18px_45px_rgba(251,113,133,0.16)]'
                          : 'border-rose-200/70 hover:border-[#FB7185]/50 bg-white/62 shadow-[0_12px_34px_rgba(120,72,54,0.08)]'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        premiumWrap ? 'border-[#FB7185] bg-[#FB7185]' : 'border-rose-200/90 bg-white/70'
                      }`}>
                        {premiumWrap && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div>
                        <p className="font-semibold text-[#3A2A2A]">Premium Wrap</p>
                        <p className="text-sm text-stone-600 mt-1">Luxury gift wrapping with premium paper, satin ribbon & tissue</p>
                        <p className="text-[#FB7185] font-semibold mt-2">+$20</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 5: Delivery */}
              {currentStep === 5 && (
                <div>
                  <h2 className="font-serif text-xl md:text-2xl font-semibold text-[#3A2A2A] mb-6">
                    Delivery or Pickup
                  </h2>
                  <div className="space-y-4 mb-8">
                    <button
                      onClick={() => setDeliveryMethod('delivery')}
                      className={`w-full p-6 rounded-2xl border-2 transition-all duration-200 text-left flex items-start gap-4 ${
                        deliveryMethod === 'delivery'
                          ? 'border-[#FB7185] bg-white/82 shadow-[0_18px_45px_rgba(251,113,133,0.16)]'
                          : 'border-rose-200/70 hover:border-[#FB7185]/50 bg-white/62 shadow-[0_12px_34px_rgba(120,72,54,0.08)]'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        deliveryMethod === 'delivery' ? 'border-[#FB7185] bg-[#FB7185]' : 'border-rose-200/90 bg-white/70'
                      }`}>
                        {deliveryMethod === 'delivery' && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div>
                        <p className="font-semibold text-[#3A2A2A]">Delivery</p>
                        <p className="text-sm text-stone-600 mt-1">Los Angeles & LA County</p>
                        <p className="text-[#FB7185] font-semibold mt-2">From $10</p>
                        <p className="text-xs text-stone-500">Price calculated based on distance</p>
                      </div>
                    </button>

                    <button
                      onClick={() => setDeliveryMethod('pickup')}
                      className={`w-full p-6 rounded-2xl border-2 transition-all duration-200 text-left flex items-start gap-4 ${
                        deliveryMethod === 'pickup'
                          ? 'border-[#FB7185] bg-white/82 shadow-[0_18px_45px_rgba(251,113,133,0.16)]'
                          : 'border-rose-200/70 hover:border-[#FB7185]/50 bg-white/62 shadow-[0_12px_34px_rgba(120,72,54,0.08)]'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        deliveryMethod === 'pickup' ? 'border-[#FB7185] bg-[#FB7185]' : 'border-rose-200/90 bg-white/70'
                      }`}>
                        {deliveryMethod === 'pickup' && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div>
                        <p className="font-semibold text-[#3A2A2A]">Pickup</p>
                        <p className="text-sm text-stone-600 mt-1">Glendale location</p>
                        <p className="text-[#FB7185] font-semibold mt-2">Free</p>
                      </div>
                    </button>
                  </div>

                  {/* Address Input (only for delivery) */}
                  {deliveryMethod === 'delivery' && (
                    <div className="bg-white/68 rounded-2xl border border-rose-200/70 p-6 mb-8 shadow-[0_14px_40px_rgba(120,72,54,0.10)] backdrop-blur-xl">
                      <Label className="text-[#3A2A2A] font-medium mb-2 block">Delivery Address (Optional)</Label>
                      <Input
                        placeholder="Street address, City"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="bg-white/72 border-rose-200/80 text-[#3A2A2A] placeholder:text-stone-400 focus:border-[#FB7185]"
                      />
                      <div className="flex items-center gap-2 mt-2">
                        {fetchingQuote ? (
                          <p className="text-xs text-stone-600 flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Calculating delivery...
                          </p>
                        ) : deliveryQuote?.distanceMiles ? (
                          <p className="text-xs text-green-400">
                            {deliveryQuote.distanceMiles.toFixed(1)} miles — ${deliveryQuote.deliveryPrice} delivery
                          </p>
                        ) : (
                          <p className="text-xs text-stone-500">
                            {deliveryNote || 'Address helps us calculate delivery price'}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Optional Contact Info */}
                  <div className="bg-white/68 rounded-2xl border border-rose-200/70 p-6 shadow-[0_14px_40px_rgba(120,72,54,0.10)] backdrop-blur-xl">
                    <h3 className="font-medium text-[#3A2A2A] mb-4">Additional Details (Optional)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-stone-600 text-sm mb-1.5 block">Your Name</Label>
                        <Input
                          placeholder="Name"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          className="bg-white/72 border-rose-200/80 text-[#3A2A2A] placeholder:text-stone-400 focus:border-[#FB7185]"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-600 text-sm mb-1.5 block">Phone Number</Label>
                        <Input
                          placeholder="Phone"
                          value={customerPhone}
                          onChange={(e) => setCustomerPhone(e.target.value)}
                          className="bg-white/72 border-rose-200/80 text-[#3A2A2A] placeholder:text-stone-400 focus:border-[#FB7185]"
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <Label className="text-stone-600 text-sm mb-1.5 block">Preferred Date & Time</Label>
                      <Input
                        placeholder="e.g., Saturday 2pm, July 15"
                        value={preferredDateTime}
                        onChange={(e) => setPreferredDateTime(e.target.value)}
                        className="bg-white/72 border-rose-200/80 text-[#3A2A2A] placeholder:text-stone-400 focus:border-[#FB7185]"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-600 text-sm mb-1.5 block">Special Notes</Label>
                      <Textarea
                        placeholder="Gift message, special instructions, etc."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="bg-white/72 border-rose-200/80 text-[#3A2A2A] placeholder:text-stone-400 focus:border-[#FB7185] min-h-[80px]"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8">
                {currentStep > 1 ? (
                  <Button
                    variant="outline"
                    onClick={() => handleStepChange(currentStep - 1)}
                    className="border-[#FB7185]/30 bg-white/62 text-[#3A2A2A] hover:bg-white/82 hover:text-[#FB7185] shadow-[0_14px_40px_rgba(120,72,54,0.10)] backdrop-blur-xl"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                ) : (
                  <div />
                )}

                {currentStep < 5 ? (
                  <Button
                    onClick={() => handleStepChange(currentStep + 1)}
                    disabled={!canProceed()}
                    className="bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 border-0 shadow-[0_18px_55px_rgba(245,188,177,0.38)]"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSendOrder}
                    disabled={!selectedVariety || savingOrder}
                    className="bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 border-0 shadow-[0_18px_55px_rgba(245,188,177,0.38)]"
                  >
                    {savingOrder ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <MessageCircle className="w-4 h-4 mr-2" />
                    )}
                    Send Order to WhatsApp
                  </Button>
                )}
              </div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                <Card className="border-rose-200/70 bg-white/68 overflow-hidden shadow-[0_18px_55px_rgba(120,72,54,0.12)] backdrop-blur-xl">
                  <div className="bg-gradient-to-r from-[#FB7185]/14 to-[#F5BCB1]/16 px-6 py-4 border-b border-rose-200/70">
                    <h3 className="font-serif text-lg font-semibold text-[#FB7185]">Order Summary</h3>
                  </div>
                  <CardContent className="p-6">
                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between items-start">
                        <span className="text-stone-600 text-sm">Rose Variety</span>
                        <span className="font-medium text-[#3A2A2A] text-right">
                          {selectedVariety 
                            ? roseCatalog.find(r => r.id === selectedVariety)?.name 
                            : '—'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600 text-sm">Stem Length</span>
                        <span className="font-medium text-[#3A2A2A]">{stemLength} cm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600 text-sm">Quantity</span>
                        <span className="font-medium text-[#3A2A2A]">{quantity} stems</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600 text-sm">Wrapping</span>
                        <span className="font-medium text-[#3A2A2A]">
                          {premiumWrap ? 'Premium (+$20)' : 'Ribbon bow'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600 text-sm">Fulfillment</span>
                        <span className="font-medium text-[#3A2A2A]">
                          {deliveryMethod === 'delivery' 
                            ? `Delivery ($${pricing.deliveryFee})` 
                            : 'Pickup (Free)'}
                        </span>
                      </div>
                    </div>

                    <div className="border-t border-rose-200/70 pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500">Bouquet</span>
                        <span className="text-stone-600">${pricing.basePrice}</span>
                      </div>
                      {premiumWrap && (
                        <div className="flex justify-between text-sm">
                          <span className="text-stone-500">Premium Wrap</span>
                          <span className="text-stone-600">+${pricing.wrapPrice}</span>
                        </div>
                      )}
                      {deliveryMethod === 'delivery' && (
                        <div className="flex justify-between text-sm">
                          <span className="text-stone-500">
                            Delivery {fetchingQuote && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}
                          </span>
                          <span className="text-stone-600">+${pricing.deliveryFee}</span>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-rose-200/70 mt-4 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-[#3A2A2A]">Estimated Total</span>
                        <span className="font-serif text-2xl font-semibold text-[#FB7185]">
                          ${pricing.total}
                        </span>
                      </div>
                      {deliveryMethod === 'delivery' && deliveryNote && (
                        <p className="text-xs text-stone-500 mt-2">
                          {deliveryNote}
                        </p>
                      )}
                    </div>

                    {/* Payment Buttons */}
                    <div className="mt-6 space-y-3">
                      {/* WhatsApp - Always enabled */}
                      {currentStep === 5 && (
                        <Button
                          onClick={handleSendOrder}
                          disabled={!selectedVariety || savingOrder}
                          className="w-full bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 lg:hidden border-0 shadow-[0_18px_55px_rgba(245,188,177,0.38)]"
                        >
                          {savingOrder ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <MessageCircle className="w-4 h-4 mr-2" />
                          )}
                          Send Order to WhatsApp
                        </Button>
                      )}
                      
                      {/* Online Payment - Always visible but disabled until Stripe configured */}
                      <Button
                        disabled={true}
                        className="w-full bg-stone-200 text-stone-500 cursor-not-allowed border-0"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Online payment coming soon
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <p className="text-xs text-stone-500 text-center mt-4">
                  Payment: Zelle & Apple Cash<br />
                  Confirmed via WhatsApp
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Mobile CTA */}
        {currentStep === 5 && (
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/85 border-t border-rose-200/70 shadow-[0_-10px_40px_rgba(0,0,0,0.08)] backdrop-blur-[20px] lg:hidden z-40">
            <Button
              onClick={handleSendOrder}
              disabled={!selectedVariety || savingOrder}
              className="w-full bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 py-6 text-base border-0 shadow-[0_18px_55px_rgba(245,188,177,0.38)]"
            >
              {savingOrder ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <MessageCircle className="w-5 h-5 mr-2" />
              )}
              Send Order to WhatsApp — ${pricing.total}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BuildPage;
